import React, { useState, useEffect } from 'react';
import { IntegrationRequest } from '../../lib/tenant-api';
import Modal from '../Modal';
import type { IntegrationConfigProps } from './types';

export const HubspotConfig: React.FC<IntegrationConfigProps> = ({
  isOpen,
  onClose,
  integration,
  createIntegrationMutation,
  updateIntegrationMutation
}) => {
  const [formData, setFormData] = useState({
    name: '',
    access_token: '',
    is_enabled: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!integration;

  useEffect(() => {
    if (integration) {
      setFormData({
        name: integration.name,
        access_token: integration.config.access_token || '',
        is_enabled: integration.is_enabled,
      });
    } else {
      setFormData({
        name: '',
        access_token: '',
        is_enabled: true,
      });
    }
    setErrors({});
  }, [integration, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Integration name is required';
    }

    if (!formData.access_token.trim()) {
      newErrors.access_token = 'Access token is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const integrationData: IntegrationRequest = {
      integration_type: 'hubspot',
      name: formData.name,
      config: {
        access_token: formData.access_token,
      },
      is_enabled: formData.is_enabled,
    };

    if (isEditing && integration) {
      updateIntegrationMutation.mutate({
        id: integration.id,
        data: integrationData,
      });
    } else {
      createIntegrationMutation.mutate(integrationData);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      return newData;
    });

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const isLoading = createIntegrationMutation.isLoading || updateIntegrationMutation.isLoading;
  return <Modal
    isOpen={isOpen}
    onClose={handleClose}
    title={isEditing ? `Edit Hubspot Integration` : `Connect Hubspot`}
  >
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Integration Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
          Integration Name
        </label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
          placeholder="e.g., My Hubspot Integration"
          disabled={isLoading}
        />
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="access_token" className="block text-sm font-medium text-gray-700 mb-2">
          Access Token
        </label>
        <input
          type="text"
          id="access_token"
          value={formData.access_token}
          onChange={(e) => handleInputChange('access_token', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.access_token ? 'border-red-300' : 'border-gray-300'
            }`}
          disabled={isLoading}
        />
        {errors.access_token && <p className="mt-1 text-sm text-red-600">{errors.access_token}</p>}
      </div>

      <div className="bg-indigo-50 border border-indigo-200  p-4">
        <h4 className="text-sm font-medium text-indigo-800 mb-3">
          How to Add a Custom "Lead Source" Contact Property in HubSpot for CRM Lead Logging
        </h4>
        <div className="text-sm text-indigo-700 space-y-4">
          <p>Here's how to create the custom property and add "LAWMA" as an option:</p>

          <div>
            <h5 className="font-semibold mb-1">1. Navigate to Properties Settings</h5>
            <p>Click the <strong>Settings (gear icon)</strong> in the top navigation bar. In the left sidebar, go to <strong>Data Management → Properties</strong>.</p>
          </div>

          <div>
            <h5 className="font-semibold mb-1">2. Create the Property</h5>
            <p>Make sure the <strong>Contact properties</strong> tab is selected at the top, then click the <strong>Create property</strong> button.</p>
          </div>

          <div>
            <h5 className="font-semibold mb-1">3. Configure Basic Info</h5>
            <ul className="list-disc list-inside ml-2 space-y-1 mt-1">
              <li><strong>Object type:</strong> Contact (should be pre-selected)</li>
              <li><strong>Group:</strong> You can use "Contact information" or create a custom group</li>
              <li><strong>Label:</strong> Enter <code>Lead Source</code></li>
              <li><strong>Internal name:</strong> This will auto-populate as <code>lead_source</code></li>
              <li><strong>Description:</strong> (Optional) "Source of the lead"</li>
            </ul>
          </div>

          <div>
            <h5 className="font-semibold mb-1">4. Set the Field Type</h5>
            <p><strong>Field type:</strong> Select <strong>Dropdown select</strong> (or Multiple checkboxes if a contact can have multiple lead sources).</p>
          </div>

          <div>
            <h5 className="font-semibold mb-1">5. Add Your Options</h5>
            <p className="mb-1">In the Options section, click <strong>Add an option</strong>:</p>
            <ul className="list-disc list-inside ml-2 space-y-1">
              <li>Enter <strong>LAWMA</strong> as one of your values</li>
              <li>Add any other lead source options needed (e.g., "Website", "Referral")</li>
              <li>Drag to reorder options as needed</li>
            </ul>
          </div>

          <div>
            <h5 className="font-semibold mb-1">6. Save</h5>
            <p>Click <strong>Create</strong> to save your new property.</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {isEditing ? 'Updating...' : 'Connecting...'}
            </>
          ) : (
            isEditing ? 'Update Integration' : 'Connect Hubspot'
          )}
        </button>
      </div>
    </form>
  </Modal>

}
